import { Component, Inject } from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Observable } from "rxjs";
import { AdminDodDefinitionService } from "../../api/services/admin-dod-definition.service";
import { DodRenameKind } from "../../api/models/dod-rename-kind";
import { DodRenameResult } from "../../api/models/dod-rename-result";
import { DialogResult, DialogResultType } from "../DialogResult";

export interface DodRenameDialogData {
  kind: DodRenameKind;
  id: number;
  // What is being renamed, for the heading: e.g. "selector", "key", "output name".
  what: string;
  current: string;
}

// Admin DoD Definition > rename a selector / key / header. Always previews first: saved layouts store these values as
// text, so the server rewrites them in the same save - this dialog shows how many layouts and recurring exports that
// touches before anything is changed (and the server refuses the apply if the count changed since the preview).
@Component({
  selector: "dod-rename-dialog",
  templateUrl: "./dod-rename-dialog.component.html",
})
export class DodRenameDialogComponent {
  public Result: DialogResult = { ResultType: DialogResultType.Cancel, ResultData: null };
  public newValue: string;
  public preview: DodRenameResult | null = null;
  public previewedValue = "";
  public busy = false;
  public error = "";

  constructor(
    public dialogRef: MatDialogRef<DodRenameDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DodRenameDialogData,
    private service: AdminDodDefinitionService
  ) {
    this.newValue = data.current;
  }

  // The preview only counts if it was made for the value currently in the box.
  get previewIsCurrent(): boolean {
    return this.preview !== null && this.previewedValue === this.newValue.trim();
  }

  onPreview(): void {
    this.run(true);
  }

  onApply(): void {
    this.run(false);
  }

  private run(dryRun: boolean): void {
    this.busy = true;
    this.error = "";
    this.service
      .adminDodDefinitionRename({
        kind: this.data.kind,
        id: this.data.id,
        newValue: this.newValue,
        dryRun,
        expectedLayoutCount: dryRun || !this.preview ? undefined : this.preview.affectedLayoutCount,
      })
      .subscribe({
        next: (r) => {
          this.busy = false;
          if (dryRun) {
            this.preview = r.outcome;
            this.previewedValue = this.newValue.trim();
          } else {
            this.Result.ResultType = DialogResultType.Ok;
            this.Result.ResultData = r;
            this.dialogRef.close(this.Result);
          }
        },
        error: (e) => {
          this.busy = false;
          this.preview = null;
          this.error = (e && (e.error || e.message)) || "Rename failed";
          if (typeof this.error !== "string") this.error = "Rename failed";
        },
      });
  }

  onCancelClick(): void {
    this.dialogRef.close(this.Result);
  }

  static showDialog(dialog: MatDialog, data: DodRenameDialogData): Observable<DialogResult> {
    return dialog.open(DodRenameDialogComponent, { disableClose: false, width: "640px", data }).afterClosed();
  }
}
